import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Share } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check if user dismissed before (respect for 7 days)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // Detect iOS Safari
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    setIsIOS(isiOS);

    if (isiOS) {
      // Show iOS guide after a delay
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Chrome/Edge: capture beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setShowBanner(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa-install-dismissed', String(Date.now()));
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-4 left-4 right-4 z-[60] sm:left-auto sm:right-4 sm:bottom-4 sm:w-[360px]"
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl shadow-black/10 dark:shadow-black/40 border border-gray-200 dark:border-gray-800 overflow-hidden">
            {showIOSGuide ? (
              /* iOS Safari guide */
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Install on iOS</p>
                  <button onClick={handleDismiss} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                      <Share className="w-3.5 h-3.5 text-blue-500" />
                    </div>
                    <span>Tap the <strong className="text-gray-900 dark:text-white">Share</strong> button in Safari</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center shrink-0">
                      <span className="text-xs">+</span>
                    </div>
                    <span>Select <strong className="text-gray-900 dark:text-white">Add to Home Screen</strong></span>
                  </div>
                </div>
              </div>
            ) : (
              /* Standard install prompt */
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Logo */}
                  <div className="w-11 h-11 rounded-xl bg-orange-50 dark:bg-gray-800 border border-orange-100 dark:border-gray-700 flex items-center justify-center shrink-0">
                    <svg width={24} height={24} viewBox="30 50 300 330" fill="none">
                      <defs>
                        <linearGradient id="ip-top" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#FFEEE4"/><stop offset="100%" stopColor="#FFE4D6"/></linearGradient>
                        <linearGradient id="ip-left" x1="100%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#FDBA74"/><stop offset="100%" stopColor="#FB923C"/></linearGradient>
                        <linearGradient id="ip-right" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#F97316"/><stop offset="100%" stopColor="#EA580C"/></linearGradient>
                      </defs>
                      <g><path d="M40 250 L40 340 L100 370 L100 280 Z" fill="url(#ip-left)"/><path d="M100 280 L100 370 L160 340 L160 250 Z" fill="url(#ip-right)"/><path d="M40 250 L100 220 L160 250 L100 280 Z" fill="url(#ip-top)"/></g>
                      <g><path d="M120 180 L120 340 L180 370 L180 210 Z" fill="url(#ip-left)"/><path d="M180 210 L180 370 L240 340 L240 180 Z" fill="url(#ip-right)"/><path d="M120 180 L180 150 L240 180 L180 210 Z" fill="url(#ip-top)"/></g>
                      <g><path d="M200 90 L200 340 L260 370 L260 120 Z" fill="url(#ip-left)"/><path d="M260 120 L260 370 L320 340 L320 90 Z" fill="url(#ip-right)"/><path d="M200 90 L260 60 L320 90 L260 120 Z" fill="url(#ip-top)"/></g>
                    </svg>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Install DayDrive</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Add to your home screen for quick access</p>
                  </div>

                  <button onClick={handleDismiss} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleDismiss}
                    className="flex-1 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Not now
                  </button>
                  <button
                    onClick={handleInstall}
                    className="flex-1 py-2 text-xs font-medium text-white bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg hover:from-orange-600 hover:to-amber-600 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Install
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
